import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Clock, Wind } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { BookButton, CallButton } from "@/components/Buttons";
import { Reveal } from "@/components/Reveal";
import { Accordion } from "@/components/Accordion";
import { SEDATION_NOTE, SERVICES, SITE_URL } from "@/data";
import { SERVICE_PAGES } from "@/serviceContent";
import { useSEO } from "@/hooks/useSEO";

const RELATED_COUNT = 3;

const ServiceHero = ({ service, intro }) => (
  <section className="relative overflow-hidden bg-sage pb-20 pt-36 lg:pt-40">
    <img src={service.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-r from-sage via-sage/90 to-sage/60" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <Link to="/services" data-testid="service-back-link" className="group inline-flex items-center gap-2 font-sans text-sm text-fern transition-colors hover:text-forest">
        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
        All treatments
      </Link>
      <Reveal>
        <p className="mt-8 font-mono text-xs tracking-[0.3em] uppercase text-leafmute">{service.tag} · Canberra City</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h1 data-testid="service-title" className="mt-4 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          {service.name}
        </h1>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-fern sm:text-lg">{intro}</p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-9 flex flex-wrap items-center gap-4">
          <BookButton testid="service-hero-book-button">Book {service.name.toLowerCase()}</BookButton>
          <CallButton testid="service-hero-call-button" light={false} />
          <span className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-fern">
            <Clock className="h-3.5 w-3.5 text-forest" /> {service.duration}
          </span>
        </div>
      </Reveal>
    </div>
  </section>
);

const ServiceContent = ({ service, page }) => (
  <div className="lg:col-span-7">
    {page.sections.map((sec, i) => (
      <Reveal key={sec.h} delay={0.05 * i}>
        <div className="mb-12">
          <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">{sec.h}</h2>
          <p className="mt-4 font-sans text-base leading-relaxed text-fern">{sec.p}</p>
        </div>
      </Reveal>
    ))}
    <Reveal>
      <h2 className="mt-4 font-serif text-2xl tracking-tight text-ink sm:text-3xl">Things people ask about {service.name.toLowerCase()}</h2>
      <div className="mt-6">
        <Accordion items={page.faqs} testid="service-faq" itemTestid="service-faq-item" dark={false} />
      </div>
    </Reveal>
  </div>
);

const ServiceSidebar = ({ goodFor, related, slug }) => (
  <aside className="lg:col-span-5">
    <div className="lg:sticky lg:top-28">
      {slug !== "conscious-sedation" && (
        <Reveal>
          <Link to="/services/conscious-sedation" data-testid="service-sedation-note" className="group mb-6 flex items-start gap-3 rounded-2xl border border-ink/10 bg-white px-5 py-4 transition-[border-color] duration-300 hover:border-leaf/50">
            <Wind className="mt-0.5 h-4 w-4 shrink-0 text-leafdeep" />
            <p className="font-sans text-xs leading-relaxed text-fern sm:text-sm">{SEDATION_NOTE} <span className="font-medium text-leafdeep group-hover:underline">Learn more</span></p>
          </Link>
        </Reveal>
      )}
      <Reveal>
        <div data-testid="service-good-for" className="rounded-2xl border border-ink/10 bg-white p-8 shadow-[0_2px_20px_-8px_rgba(18,26,21,0.1)]">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-leafdeep">Worth a look if</p>
          <ul className="mt-5 space-y-3.5">
            {goodFor.map((g) => (
              <li key={g} className="flex items-start gap-3 font-sans text-sm leading-relaxed text-ink/85">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-leaf/15 text-leafdeep">
                  <Check className="h-3 w-3" />
                </span>
                {g}
              </li>
            ))}
          </ul>
          <div className="mt-8 border-t border-ink/10 pt-6">
            <p className="font-sans text-sm text-fern">Not sure if this is you? Book a check-up and Dr Gupta will give you a straight answer.</p>
            <BookButton variant="light" testid="service-sidebar-book-button" className="mt-5 w-full justify-center">Book online</BookButton>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-8 rounded-2xl bg-forest p-8">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-leaf">Also at Glebe Park Dental</p>
          <ul className="mt-5 divide-y divide-white/10">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to={`/services/${r.slug}`}
                  data-testid={`related-service-${r.slug}`}
                  className="group flex items-center justify-between py-3.5 font-sans text-sm text-cream transition-colors hover:text-leaf"
                >
                  {r.name}
                  <ArrowUpRight className="h-4 w-4 text-mist transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-leaf" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </aside>
);

const relatedTo = (service) =>
  SERVICES.filter((s) => s.slug !== service.slug)
    .sort((a, b) => (b.tag === service.tag) - (a.tag === service.tag))
    .slice(0, RELATED_COUNT);

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const page = SERVICE_PAGES[slug];

  useSEO({
    title: page?.metaTitle || "Glebe Park Dental",
    description: page?.metaDesc || "",
    path: `/services/${slug}`,
    jsonLd: page && {
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: service.name,
      description: page.metaDesc,
      url: `${SITE_URL}/services/${slug}`,
      provider: { "@type": "Dentist", name: "Glebe Park Dental", url: SITE_URL },
    },
  });

  if (!service || !page) return <Navigate to="/services" replace />;

  return (
    <main className="bg-cream" data-testid="service-page">
      <Nav />
      <ServiceHero service={service} intro={page.intro} />
      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-16">
          <ServiceContent service={service} page={page} />
          <ServiceSidebar goodFor={page.goodFor} related={relatedTo(service)} slug={slug} />
        </div>
      </section>
      <Footer />
    </main>
  );
}
